import { RouteRecordRaw } from 'vue-router';
import { requireAuth } from '@/common/router/navigationGuard';
import { VIEW_MODE } from '@/common/define/common.define';
import { extractAccessibleRoutes } from '@/common/auth/auth.utils';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { BASE_MENU_VALUE } from '@/common/components/organisms/baseMenu/baseMenu.define';
import {
  requireDomainTargetOrTutorial,
  requireInstances,
  setDayjsTimezone,
  validateDatabaseOverviewPath,
} from '@/common/router/authGuard';
import BaseViewport from '@/common/components/templates/baseViewport/BaseViewport.vue';
import { VIEWPORT_TYPE } from '@/common/components/templates/baseViewport/baseViewport.define';

const PREFIX_ALTIBASE = 'altibase_';

const altibaseMenu: RouteRecordRaw[] = [
  {
    path: '/altibase/',
    name: `${PREFIX_ALTIBASE}Real Time Monitoring`,
    component: BaseViewport,
    children: [
      {
        path: 'multiView',
        name: `${PREFIX_ALTIBASE}Multi View`,
        component: () => import('@/database/views/multiView/altibase/AltibaseMultiView.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [
      requireAuth,
      () => requireDomainTargetOrTutorial('database'),
      setDayjsTimezone,
      requireInstances,
      validateDatabaseOverviewPath,
    ],
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD,
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.REALTIME,
    },
  },
  {
    path: '/altibase/',
    name: `${PREFIX_ALTIBASE}SQL Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'topNAnalysis',
        name: `${PREFIX_ALTIBASE}Top N Analysis`,
        component: () => import('@/database/views/topNAnalysis/altibase/TopNAnalysis.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('database')],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_ALTIBASE_PA,
        [VIEW_MODE.MAXGAUGE]:
          ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALTIBASE_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
  {
    path: '/altibase/',
    name: `${PREFIX_ALTIBASE}Performance Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'trendAnalysis',
        name: `${PREFIX_ALTIBASE}Trend Analysis`,
        component: () => import('@/database/views/trendAnalysis/altibase/TrendAnalysis.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'replication',
        name: `${PREFIX_ALTIBASE}Replication Analysis`,
        component: () => import('@/altibase/views/replicationView/ReplicationView.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('database')],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_ALTIBASE_PA,
        [VIEW_MODE.MAXGAUGE]:
          ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALTIBASE_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
];

const subMenuMappingAltibase = {
  instanceView: `${PREFIX_ALTIBASE}Real Time Monitoring`,
  sqlAnalysis: `${PREFIX_ALTIBASE}SQL Analysis`,
  performanceAnalysis: `${PREFIX_ALTIBASE}Performance Analysis`,
};

const getAltibaseNaviMenu = (subMenu?: string) => {
  const filteredAltibaseMenu = extractAccessibleRoutes(altibaseMenu);

  if (subMenu && subMenuMappingAltibase[subMenu]) {
    return filteredAltibaseMenu.filter((menu) => menu.name === subMenuMappingAltibase[subMenu]);
  }

  return filteredAltibaseMenu.filter((menu) => menu.name !== subMenuMappingAltibase.instanceView);
};

export { altibaseMenu, getAltibaseNaviMenu, PREFIX_ALTIBASE };
